import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { AppState } from '../../../store';
import LinearProgressBar from '../../../common/LinearProgress';
import AddCategory from './AddCategory';
import { onGetAllCategories } from '../../../store/actions/Category';
import jwtAxios from '../../../services/axiosInstance/ApiConfig';
import { getHeaders } from '../../../store/actions/Auth';
import { showErrorMessage, showSuccessMessage } from '../../../common/Toaster';

interface Column {
	id: 'id' | 'name' | 'actions';
	label: string;
	minWidth?: number;
	align?: 'right';
	format?: (value: number) => string;
}

const columns: readonly Column[] = [
	{ id: 'id', label: 'Category ID', minWidth: 170 },
	{ id: 'name', label: 'Category Name', minWidth: 170 },
	{ id: 'actions', label: 'Actions', minWidth: 170 }
];

export interface CategoryType {
	id: string;
	name: string;
}

function createData(
	id: string,
	name: string,
): CategoryType {
	return { id, name };
}
interface CategoriesProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	cancel: (from: string) => void;
	selectedCategory: CategoryType | null;
	handleSelectedCategory: (p: CategoryType) => void;
}

export default function Categories(props: CategoriesProps) {
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(10);
	const dispatch = useDispatch()
	const {categories} = useSelector<AppState, AppState['categories']>(
        ({categories}) => categories,
    );

	useEffect(() => {
		if(!categories)
			dispatch(onGetAllCategories())
	}, [dispatch, categories])

	const {loading} = useSelector<AppState, AppState['common']>(
        ({common}) => common,
    );

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	const deleteCategory = (rowId: string) => {
		jwtAxios.delete('/category/deleteCategory/' + rowId, { headers: getHeaders()})
		.then(response => {
			showSuccessMessage(response.data.message)
			dispatch(onGetAllCategories())
		}).catch((err: any) => {
			showErrorMessage(err)
		})
	}

	const rows: any =
		categories && categories.length > 0 
		? categories.map((category: any) => createData(category._id, category.name)) 
		: [];

	return (
		<>
			{props.open && <AddCategory
				setOpen={props.setOpen}
				cancel={props.cancel}
				selectedCategory={props.selectedCategory} 
			/>}
			{!props.open && 
			<Paper sx={{ width: '100%', overflow: 'hidden', padding: 0 }}>
				<TableContainer>
					{loading && <LinearProgressBar />}
					<Table stickyHeader aria-label="sticky table">
						<TableHead>
							<TableRow>
								{columns.map((column) => (
									<TableCell
										key={column.id}
										align={column.align}
										style={{ minWidth: column.minWidth, fontWeight: 600 }}
									>
										{column.label}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{rows
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map((row: any) => {
									return (
										<TableRow hover role="checkbox" tabIndex={-1} key={row.name}>
											{columns.map((column) => {
												const col_id = column.id
												let value = row[col_id];
												return (
													<TableCell key={column.id} align={column.align}>
														{col_id === 'actions' && 
															<Box display="flex" flexDirection="row">
																<Button
																	color="info"
																	variant="outlined"
																	size="small"
																	style={{marginRight: '8px'}}
																	onClick={()=> props.handleSelectedCategory(row)}
																>
																	Edit
																</Button>
																<Button
																	color="error"
																	variant="outlined"
																	size="small"
																	onClick={()=> deleteCategory(row.id)}
																>
																	Delete
																</Button>
															</Box>
														}
														{col_id !== 'actions' && column.format && typeof value === 'number'
															? column.format(value)
															: value}
													</TableCell>
												);
											})}
										</TableRow>
									);
								})}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={[10, 25, 100]}
					component="div"
					count={rows.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</Paper>}
		</>
	);
}
